import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Smartphone, Tv, ChevronDown, Loader2, 
  CheckCircle, AlertCircle, Zap, Clock,
  XCircle, RefreshCw, FileText, RotateCcw,
  ChevronUp, X, Copy
} from 'lucide-react';

import { API } from "../lib/api";

const RechargeCard = ({ user, stats }) => {
  const [rechargeType, setRechargeType] = useState('mobile');
  const [number, setNumber] = useState('');
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recentTxns, setRecentTxns] = useState([]);
  const [allTxns, setAllTxns] = useState([]);
  const [dailyRemaining, setDailyRemaining] = useState(500);
  const [monthlyRemaining, setMonthlyRemaining] = useState(1500);
  const [showHistory, setShowHistory] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  // DTH-specific state
  const [operatorParams, setOperatorParams] = useState(null);
  const [fetchBillRequired, setFetchBillRequired] = useState(false);
  const [billData, setBillData] = useState(null);
  const [fetchingBill, setFetchingBill] = useState(false);
  const [numberError, setNumberError] = useState('');

  const prcRate = stats?.prcRate || 10;

  const fetchOperators = useCallback(async (type) => {
    setOperatorsLoading(true);
    setOperators([]);
    setSelectedOperator('');
    try {
      const res = await axios.get(`${API}/recharge/operators/${type}`);
      if (res.data.success && res.data.operators?.length) {
        setOperators(res.data.operators);
      }
    } catch {
      // silent
    } finally {
      setOperatorsLoading(false);
    }
  }, []);

  const fetchRecentTxns = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const res = await axios.get(`${API}/recharge/history/${user.uid}`);
      if (res.data.success) {
        const txns = res.data.transactions || [];
        const successful = txns
          .filter(t => t.status === 'success' || t.status === 'complete')
          .slice(0, 3);
        setRecentTxns(successful);
        setAllTxns(txns);
        if (res.data.daily_remaining !== undefined) {
          setDailyRemaining(res.data.daily_remaining);
        }
        if (res.data.monthly_remaining !== undefined) {
          setMonthlyRemaining(res.data.monthly_remaining);
        }
      }
    } catch {
      // silent
    }
  }, [user?.uid]);

  const [limitAvailablePrc, setLimitAvailablePrc] = useState(null);
  const fetchRedeemLimit = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const res = await axios.get(`${API}/redemption-limit/status/${user.uid}`);
      setLimitAvailablePrc(Number(res.data?.available_prc || 0));
    } catch (err) {
      console.error('[RechargeCard] redeem limit fetch failed:', err);
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchRedeemLimit();
  }, [fetchRedeemLimit]);

  useEffect(() => {
    fetchOperators(rechargeType);
  }, [rechargeType, fetchOperators]);

  useEffect(() => {
    fetchRecentTxns();
  }, [fetchRecentTxns]);

  const handleTypeSwitch = (type) => {
    if (type === rechargeType) return;
    setRechargeType(type);
    setNumber('');
    setAmount('');
    setResult(null);
    // Reset DTH state
    setOperatorParams(null);
    setFetchBillRequired(false);
    setBillData(null);
    setNumberError('');
  };

  // DTH: Fetch operator params when operator changes
  const handleOperatorChange = async (opId) => {
    setSelectedOperator(opId);
    setNumberError('');
    setBillData(null);

    if (rechargeType === 'dth' && opId) {
      try {
        const res = await axios.get(`${API}/recharge/operator-params/${opId}`);
        if (res.data.success && res.data.parameters?.length) {
          setOperatorParams(res.data.parameters);
          setFetchBillRequired(res.data.fetch_bill_required || false);
        } else {
          setOperatorParams(null);
          setFetchBillRequired(false);
        }
      } catch {
        setOperatorParams(null);
        setFetchBillRequired(false);
      }
    } else {
      setOperatorParams(null);
      setFetchBillRequired(false);
    }
  };

  // DTH: Validate subscriber ID against operator regex
  const validateDthNumber = (val) => {
    if (!operatorParams?.length || rechargeType !== 'dth') return true;
    const param = operatorParams[0];
    if (param.regex) {
      try {
        const regex = new RegExp(param.regex);
        if (!regex.test(val)) {
          setNumberError(param.error_message || `Invalid ${param.param_label || 'subscriber ID'} format`);
          return false;
        }
      } catch { /* invalid regex from Eko, skip */ }
    }
    setNumberError('');
    return true;
  };

  // DTH: Handle fetch bill
  const handleFetchBill = async () => {
    if (!number || !selectedOperator) {
      toast.error('Please enter subscriber ID and select operator');
      return;
    }
    if (!validateDthNumber(number)) return;

    setFetchingBill(true);
    setBillData(null);
    try {
      const res = await axios.post(`${API}/recharge/fetch-bill`, {
        user_id: user.uid,
        number: number.trim(),
        operator_id: selectedOperator
      });
      if (res.data.success) {
        setBillData(res.data.bill);
        if (res.data.bill?.amount) {
          setAmount(String(Math.min(parseInt(res.data.bill.amount) || 0, 500)));
        }
        toast.success('Bill fetched successfully');
      } else {
        toast.error(res.data.error || 'Failed to fetch bill');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to fetch bill');
    } finally {
      setFetchingBill(false);
    }
  };

  const maxAllowed = Math.min(500, dailyRemaining, monthlyRemaining);

  const handleAmountChange = (e) => {
    const raw = e.target.value;
    if (raw === '') { setAmount(''); return; }
    const num = parseInt(raw, 10);
    if (isNaN(num) || num < 0) return;
    if (num > maxAllowed) return;
    setAmount(String(num));
  };

  const handleRecharge = async () => {
    if (!number || !selectedOperator || !amount) {
      toast.error('Please fill all fields');
      return;
    }
    const amt = parseInt(amount, 10);
    if (isNaN(amt) || amt <= 0 || amt > 500) {
      toast.error('Amount must be between 1 and 500');
      return;
    }
    if (rechargeType === 'mobile' && !/^\d{10}$/.test(number)) {
      toast.error('Enter a valid 10-digit mobile number');
      return;
    }
    // DTH: validate subscriber ID against operator regex
    if (rechargeType === 'dth' && !validateDthNumber(number)) {
      return;
    }
    // DTH: if fetchBill required but not fetched
    if (rechargeType === 'dth' && fetchBillRequired && !billData) {
      toast.error('Please fetch bill first before recharging');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const payload = {
        user_id: user.uid,
        recharge_type: rechargeType,
        number: number.trim(),
        operator_id: selectedOperator,
        operator_name: operators.find(op => String(op.operator_id ?? op.id) === String(selectedOperator))?.name || '',
        amount: amt
      };
      // Pass billfetchresponse if available (DTH with fetchBill)
      if (rechargeType === 'dth' && billData?.billfetchresponse) {
        payload.billfetchresponse = billData.billfetchresponse;
      }

      const res = await axios.post(`${API}/recharge/initiate`, payload);

      if (res.data.success) {
        setResult({ success: true, message: res.data.message });
        toast.success(res.data.message);
        setNumber('');
        setAmount('');
        setBillData(null);
        fetchRecentTxns();
        fetchRedeemLimit();
        if (res.data.amount) {
          setDailyRemaining(prev => Math.max(0, prev - res.data.amount));
        }
      } else {
        setResult({ success: false, message: res.data.message });
        toast.error(res.data.message);
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.detail || 'Technical error. Please try again later.';
      setResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const prcEstimate = amount && parseInt(amount, 10) > 0
    ? Math.ceil((parseInt(amount, 10) + 10) * prcRate)
    : 0;

  const limitExceeded = limitAvailablePrc != null && amount && parseInt(amount, 10) * prcRate > limitAvailablePrc;

  const isFormValid = number && selectedOperator && amount && parseInt(amount, 10) > 0 && parseInt(amount, 10) <= maxAllowed && !numberError && !limitExceeded
    && (rechargeType !== 'dth' || !fetchBillRequired || billData);

  // View receipt for a transaction
  const handleViewReceipt = async (requestId) => {
    setReceiptLoading(true);
    try {
      const res = await axios.get(`${API}/recharge/receipt/${requestId}?user_id=${user.uid}`);
      if (res.data.success) {
        setReceiptData(res.data.receipt);
      } else {
        toast.error(res.data.error || 'Failed to load receipt');
      }
    } catch {
      toast.error('Failed to load receipt');
    } finally {
      setReceiptLoading(false);
    }
  };

  // Retry a failed recharge
  const handleRetry = async (requestId) => {
    try {
      const res = await axios.post(`${API}/recharge/retry/${requestId}`, { user_id: user.uid });
      if (res.data.success && res.data.retry_data) {
        const rd = res.data.retry_data;
        setRechargeType(rd.recharge_type);
        setNumber(rd.number);
        setSelectedOperator(rd.operator_id);
        setAmount(String(rd.amount));
        setShowHistory(false);
        setResult(null);
        toast.success('Recharge details loaded. Tap "Recharge Now" to retry.');
      } else {
        toast.error(res.data.error || 'Cannot retry this transaction');
      }
    } catch {
      toast.error('Failed to load retry data');
    }
  };

  // Status icon and color helper
  const getStatusDisplay = (status) => {
    const map = {
      success: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Success' },
      complete: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Success' },
      pending: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: 'Processing' },
      failed: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Failed' },
      refunded: { icon: RefreshCw, color: 'text-orange-400', bg: 'bg-orange-500/10', label: 'Refunded' },
      refund_pending: { icon: AlertCircle, color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Refund Pending' },
    };
    return map[status] || { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-500/10', label: status || 'Unknown' };
  };

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      data-testid="recharge-card"
      className="pr-dark-scope rounded-2xl p-5 overflow-hidden relative"
      style={{
        background: 'var(--pr-charcoal)',
        border: '1px solid #353C47',
        boxShadow: '0 10px 26px -14px rgba(32, 37, 45, 0.6)'
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#246BCE]/20 flex items-center justify-center">
          <Zap className="w-4 h-4 text-[#7FB0F5]" />
        </div>
        <span className="text-white font-semibold text-sm">Quick Recharge</span>
      </div>

      {/* Mobile / DTH Toggle */}
      <div className="flex bg-[#2A313B] rounded-lg p-1 mb-4" data-testid="recharge-type-toggle">
        {[
          { key: 'mobile', label: 'Mobile', icon: Smartphone },
          { key: 'dth', label: 'DTH', icon: Tv }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => handleTypeSwitch(key)}
            data-testid={`recharge-toggle-${key}`}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-all ${
              rechargeType === key
                ? 'bg-[#246BCE] text-white border border-[#246BCE]'
                : 'text-gray-400 hover:text-white border border-transparent'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Number Input */}
      <div className="mb-3">
        <input
          type="tel"
          inputMode="numeric"
          maxLength={rechargeType === 'mobile' ? 10 : 20}
          placeholder={
            rechargeType === 'mobile'
              ? 'Enter 10-digit mobile number'
              : (operatorParams?.[0]?.param_label || 'Enter subscriber ID')
          }
          value={number}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, '');
            setNumber(val);
            if (rechargeType === 'dth' && val.length >= 3) validateDthNumber(val);
            else setNumberError('');
            setBillData(null);
          }}
          data-testid="recharge-number-input"
          className={`w-full bg-[#2A313B] border rounded-lg px-3 py-3 text-white text-sm placeholder-gray-500 focus:outline-none transition-colors ${
            numberError ? 'border-red-500/50 focus:border-red-500/70' : 'border-[#3E4754] focus:border-[#4F8DE0]'
          }`}
        />
        {numberError && (
          <p className="text-red-400 text-[10px] mt-1 pl-1" data-testid="number-error">{numberError}</p>
        )}
      </div>

      {/* DTH: Fetch Bill Button (when required) */}
      {rechargeType === 'dth' && fetchBillRequired && selectedOperator && number && !billData && (
        <button
          onClick={handleFetchBill}
          disabled={fetchingBill || !number || !!numberError}
          data-testid="fetch-bill-btn"
          className={`w-full py-2 rounded-lg text-xs font-semibold mb-3 flex items-center justify-center gap-2 transition-all ${
            fetchingBill || !number || !!numberError
              ? 'pr-btn-disabled'
              : 'bg-[#D6A638]/15 text-amber-400 border border-[#D6A638]/40 hover:bg-[#D6A638]/25'
          }`}
        >
          {fetchingBill ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching Bill...</> : 'Fetch Bill Details'}
        </button>
      )}

      {/* DTH: Bill Details Display */}
      {billData && (
        <div className="mb-3 p-2.5 rounded-lg bg-[#246BCE]/15 border border-[#246BCE]/30" data-testid="bill-details">
          {billData.customer_name && (
            <p className="text-[#A9C9F8] text-xs font-medium">{billData.customer_name}</p>
          )}
          {billData.amount && (
            <p className="text-gray-400 text-[10px] mt-0.5">Bill Amount: ₹{billData.amount}</p>
          )}
        </div>
      )}

      {/* Operator Select */}
      <div className="mb-3 relative">
        <select
          value={selectedOperator}
          onChange={(e) => handleOperatorChange(e.target.value)}
          data-testid="recharge-operator-select"
          className="w-full bg-[#2A313B] border border-[#3E4754] rounded-lg px-3 py-3 text-sm appearance-none focus:outline-none focus:border-[#4F8DE0] transition-colors"
          style={{ color: selectedOperator ? '#F3F5F8' : '#98A2B0' }}
        >
          <option value="" style={{ background: '#20252D', color: '#98A2B0' }}>
            {operatorsLoading ? 'Loading operators...' : 'Select Operator'}
          </option>
          {operators.map(op => (
            <option
              key={op.operator_id}
              value={op.operator_id}
              style={{ background: '#20252D', color: '#F3F5F8' }}
            >
              {op.name}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-3 pointer-events-none">
          {operatorsLoading
            ? <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            : <ChevronDown className="w-4 h-4 text-gray-400" />
          }
        </div>
      </div>

      {/* Amount Input — hard capped at 500 */}
      <div className="mb-4">
        <div className="relative">
          <span className="absolute left-3 top-2.5 text-gray-400 text-sm font-medium">₹</span>
          <input
            type="text"
            inputMode="numeric"
            maxLength={3}
            placeholder="Amount"
            value={amount}
            onChange={handleAmountChange}
            data-testid="recharge-amount-input"
            className="w-full bg-[#2A313B] border border-[#3E4754] rounded-lg pl-7 pr-3 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#4F8DE0] transition-colors"
          />
        </div>
        {prcEstimate > 0 && (
          <>
          <p className="text-gray-500 text-[10px] mt-1 pl-1" data-testid="recharge-prc-estimate">
            Estimated: ~{prcEstimate.toLocaleString('en-IN')} PRC (incl. ₹10 processing fee)
          </p>
          <p className="text-[10px] mt-0.5 pl-1" style={{ color: '#D6A638' }} data-testid="recharge-service-charge-note">
            + 20% cash service charge (₹{(parseInt(amount, 10) * 0.2).toFixed(2)}) payable after a successful recharge
          </p>
          </>
        )}
        {limitAvailablePrc != null && (
          <p className="text-[10px] mt-1 pl-1" style={{ color: '#B4BDC9' }} data-testid="recharge-limit-remaining">
            Remaining Redeem Limit: <span className="font-semibold tabular-nums" style={{ color: '#7FB0F5' }}>{limitAvailablePrc.toLocaleString('en-IN')} PRC (₹{(limitAvailablePrc / prcRate).toLocaleString('en-IN', { maximumFractionDigits: 2 })})</span>
          </p>
        )}
        {limitExceeded && (
          <p className="text-red-400 text-[11px] mt-1 pl-1" data-testid="recharge-limit-error">
            This recharge needs {(parseInt(amount, 10) * prcRate).toLocaleString('en-IN')} PRC of redeem limit, but only {limitAvailablePrc.toLocaleString('en-IN')} PRC is remaining. Enter a smaller amount.
          </p>
        )}
      </div>

      {/* Recharge Button */}
      <button
        onClick={handleRecharge}
        disabled={loading || !isFormValid}
        data-testid="recharge-submit-btn"
        className={`w-full py-3 rounded-xl text-sm font-semibold transition-[background-color,transform] flex items-center justify-center gap-2 ${
          loading || !isFormValid
            ? 'pr-btn-disabled'
            : 'btn-pr-primary active:scale-[0.98]'
        }`}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <Zap className="w-4 h-4" />
            Recharge Now
          </>
        )}
      </button>

      {/* Result */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            data-testid="recharge-result"
            className={`mt-3 p-3 rounded-lg flex items-start gap-2 text-xs ${
              result.success
                ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                : 'bg-red-500/10 border border-red-500/20 text-red-400'
            }`}
          >
            {result.success
              ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            }
            <span>{result.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recent Successful Recharges + View All */}
      {(recentTxns.length > 0 || allTxns.length > 0) && (
        <div className="mt-4 pt-3 border-t border-[#353C47]" data-testid="recent-recharges">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-500 text-[10px] uppercase tracking-wider">Recent Recharges</p>
            {allTxns.length > 0 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                data-testid="toggle-history-btn"
                className="text-[#7FB0F5] text-[10px] font-medium flex items-center gap-1 hover:text-[#A9C9F8] transition-colors"
              >
                {showHistory ? <>Hide <ChevronUp className="w-3 h-3" /></> : <>View All ({allTxns.length}) <ChevronDown className="w-3 h-3" /></>}
              </button>
            )}
          </div>

          {!showHistory ? (
            <div className="space-y-2">
              {recentTxns.map((txn) => (
                <div
                  key={txn.request_id}
                  className="flex items-center justify-between"
                  data-testid={`recent-txn-${txn.request_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {txn.recharge_type === 'dth'
                      ? <Tv className="w-3.5 h-3.5 text-[#7FB0F5]/70 flex-shrink-0" />
                      : <Smartphone className="w-3.5 h-3.5 text-[#7FB0F5]/70 flex-shrink-0" />
                    }
                    <span className="text-white/70 text-xs truncate">{txn.number}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-emerald-400 text-xs font-semibold">
                      ₹{Number(txn.amount_inr || 0).toLocaleString('en-IN')}
                    </span>
                    <CheckCircle className="w-3 h-3 text-emerald-500/70" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1" data-testid="full-history">
              {allTxns.map((txn) => {
                const sd = getStatusDisplay(txn.status);
                const StatusIcon = sd.icon;
                const canRetry = ['failed', 'refunded', 'refund_pending'].includes(txn.status);
                return (
                  <div
                    key={txn.request_id}
                    className={`p-2 rounded-lg ${sd.bg} border border-[#353C47]`}
                    data-testid={`history-txn-${txn.request_id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {txn.recharge_type === 'dth'
                          ? <Tv className="w-3 h-3 text-[#7FB0F5]/70 flex-shrink-0" />
                          : <Smartphone className="w-3 h-3 text-[#7FB0F5]/70 flex-shrink-0" />
                        }
                        <span className="text-white/80 text-[11px] truncate">{txn.number}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-white/80 text-[11px] font-semibold">₹{txn.amount_inr}</span>
                        <StatusIcon className={`w-3 h-3 ${sd.color}`} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-[9px] flex items-center gap-1.5">
                        {formatDate(txn.created_at)}
                        {['failed', 'refunded', 'refund_pending'].includes(txn.status) && (
                          <span
                            data-testid={`refund-badge-${txn.request_id}`}
                            className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${txn.prc_refunded ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}
                          >
                            {txn.prc_refunded
                              ? `PRC Refunded${txn.prc_refund_amount ? ` · ${Math.round(txn.prc_refund_amount).toLocaleString('en-IN')}` : ''}`
                              : 'Refund in progress'}
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleViewReceipt(txn.request_id)}
                          data-testid={`receipt-btn-${txn.request_id}`}
                          className="text-[#7FB0F5] hover:text-[#A9C9F8] p-0.5"
                          title="View Receipt"
                        >
                          <FileText className="w-3 h-3" />
                        </button>
                        {canRetry && (
                          <button
                            onClick={() => handleRetry(txn.request_id)}
                            data-testid={`retry-btn-${txn.request_id}`}
                            className="text-amber-400/70 hover:text-amber-300 p-0.5"
                            title="Retry Recharge"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Receipt Modal */}
      <AnimatePresence>
        {receiptData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setReceiptData(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="receipt-modal"
              className="pr-dark-scope bg-[#20252D] border border-[#353C47] rounded-2xl p-5 w-full max-w-sm shadow-2xl"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-sm">Transaction Receipt</h3>
                <button onClick={() => setReceiptData(null)} className="text-gray-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                <div className={`text-center p-3 rounded-lg ${
                  receiptData.status === 'success' ? 'bg-emerald-500/10' :
                  receiptData.status === 'failed' ? 'bg-red-500/10' :
                  'bg-yellow-500/10'
                }`}>
                  <p className="text-2xl font-bold text-white">₹{receiptData.amount_inr}</p>
                  <p className={`text-xs font-medium mt-1 ${
                    receiptData.status === 'success' ? 'text-emerald-400' :
                    receiptData.status === 'failed' ? 'text-red-400' :
                    'text-yellow-400'
                  }`}>{receiptData.status_label}</p>
                </div>

                <div className="space-y-2 text-xs">
                  {[
                    ['Type', receiptData.type],
                    ['Number', receiptData.number],
                    ['PRC Deducted', receiptData.total_prc_deducted ? `${receiptData.total_prc_deducted} PRC` : '-'],
                    ['Eko TID', receiptData.eko_tid || '-'],
                    ['Date', formatDate(receiptData.created_at)],
                    ['Request ID', receiptData.request_id?.slice(0, 16) + '...'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-gray-500">{label}</span>
                      <span className="text-white/80 font-mono text-[11px]">{value}</span>
                    </div>
                  ))}
                  {receiptData.prc_refunded && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">PRC Refunded</span>
                      <span data-testid="receipt-refund-amount" className="text-emerald-400 font-semibold">
                        {receiptData.prc_refund_amount ? `${Math.round(receiptData.prc_refund_amount).toLocaleString('en-IN')} PRC` : 'Yes'}
                        {receiptData.refund_at ? ` · ${formatDate(receiptData.refund_at)}` : ''}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    const text = `Recharge Receipt\nType: ${receiptData.type}\nNumber: ${receiptData.number}\nAmount: ₹${receiptData.amount_inr}\nStatus: ${receiptData.status_label}\nTID: ${receiptData.eko_tid || 'N/A'}\nDate: ${receiptData.created_at}\nRef: ${receiptData.request_id}`;
                    navigator.clipboard.writeText(text);
                    toast.success('Receipt copied!');
                  }}
                  data-testid="copy-receipt-btn"
                  className="w-full py-2 rounded-lg bg-[#246BCE]/20 text-[#7FB0F5] text-xs font-medium border border-[#246BCE]/30 hover:bg-[#246BCE]/30 flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Copy className="w-3 h-3" /> Copy Receipt
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default RechargeCard;
